import { useIntl } from "react-intl";
import { useRecoilValue } from "recoil";

import { formatterService } from "../../NumberFormatter";

import ForecastChart from "./ForecastChart";
import {
  axisTicks,
  decimal,
  money,
  NIGHTS_UNIT_KEY,
  SERIES_COLORS,
} from "./constants";
import { BreakdownRow, ErrorKind, ForecastTarget } from "./services";

/** Long property names would overlap; the axis keeps them short and the tooltip has the rest. */
const shorten = (label: string, max = 14) =>
  label.length > max ? `${label.slice(0, max - 1)}…` : label;

/**
 * Where each forecast goes wrong, one bar pair per modality, in the unit of the measure. Both
 * share one axis, so a modality where the ML loses stays visible.
 *
 * Lead time takes `curve`: ordered and unevenly sampled, drawn as a line on a numeric axis.
 */
const ErrorBreakdownChart = ({
  title,
  rows,
  target,
  kind,
  maxRows,
  curve = false,
}: {
  title: string;
  rows: BreakdownRow[];
  target: ForecastTarget;
  kind: ErrorKind;
  /** Keep only the first modalities. The API sorts them worst first, so those are the ones
   *  worth reading; past a couple of dozen bars the axis is unreadable anyway. */
  maxRows?: number;
  curve?: boolean;
}) => {
  const intl = useIntl();
  const formatter = useRecoilValue(formatterService);
  if (rows.length === 0) return null;

  const [baselineKey, mlKey] =
    kind === "bias"
      ? (["baseline_bias", "ml_bias"] as const)
      : (["baseline_mae", "ml_mae"] as const);
  const isPrice = target === "adr";
  const write = (value: number, digits = 0) =>
    isPrice
      ? money(formatter, value, digits)
      : `${decimal(formatter, value, digits)} ${intl.formatMessage({
          id: NIGHTS_UNIT_KEY,
        })}`;

  const shown = (maxRows === undefined ? rows : rows.slice(0, maxRows)).map(
    (row) => ({ ...row, lead_time: Number(row.key) })
  );
  const truncated = shown.length < rows.length;
  // Over every modality, not only the drawn ones, and on distance from zero so the signed
  // view does not call the smaller bar the better one.
  const won = rows.filter(
    (row) => Math.abs(row[mlKey]) < Math.abs(row[baselineKey])
  ).length;
  const leadTimes = shown.map((point) => point.lead_time);

  return (
    <ForecastChart
      title={title}
      subtitle={`${intl.formatMessage(
        {
          id: curve
            ? "ML_FORECAST_LEAD_TIME_CHART_SUBTITLE"
            : "ML_FORECAST_BREAKDOWN_SUBTITLE",
        },
        { won, total: rows.length }
      )}${
        truncated
          ? ` ${intl.formatMessage(
              { id: "ML_FORECAST_BREAKDOWN_TRUNCATED" },
              { count: shown.length }
            )}`
          : ""
      }`}
      data={shown}
      bars={!curve}
      xKey={curve ? "lead_time" : "label"}
      xDomain={
        curve ? [Math.min(...leadTimes), Math.max(...leadTimes)] : undefined
      }
      xTicks={curve ? axisTicks(leadTimes) : undefined}
      xLabel={
        curve
          ? intl.formatMessage({ id: "ML_FORECAST_AXIS_LEAD_TIME" })
          : undefined
      }
      formatX={(value) => (curve ? `J${value}` : shorten(String(value)))}
      formatY={(value) => decimal(formatter, value)}
      formatValue={(value) => write(value, 2)}
      series={[
        {
          key: baselineKey,
          name: intl.formatMessage({ id: "ML_FORECAST_LEGEND_BASELINE" }),
          color: SERIES_COLORS.baseline,
        },
        {
          key: mlKey,
          name: intl.formatMessage({ id: "ML_FORECAST_LEGEND_ML" }),
          color: SERIES_COLORS.ml,
        },
      ]}
    />
  );
};

export default ErrorBreakdownChart;
